import {FC, useEffect, useState} from 'react'
import type React from 'react'
import {api, BroadcastNotification} from '../../services/api'
import {toast} from '../../utils/toast'

type Audience = 'all' | 'users' | 'providers'

const CARD: React.CSSProperties = {
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

const AUDIENCES: {value: Audience; label: string}[] = [
  {value: 'all', label: 'All'},
  {value: 'users', label: 'Users'},
  {value: 'providers', label: 'Providers'},
]

const audienceLabel = (a: string): string => {
  switch (a) {
    case 'users': return 'Users'
    case 'providers': return 'Providers'
    case 'admins': return 'Admins'
    default: return 'All'
  }
}

const audienceColors = (a: string): {bg: string; color: string} => {
  switch (a) {
    case 'users': return {bg: '#dcfce7', color: '#16a34a'}      // green
    case 'providers': return {bg: '#f3e8ff', color: '#9333ea'}  // purple
    case 'admins': return {bg: '#ffedd5', color: '#ea580c'}     // orange
    default: return {bg: '#dbeafe', color: '#2563eb'}           // blue
  }
}

const statusColors = (s: string): {bg: string; color: string; label: string} => {
  switch (s) {
    case 'sent': return {bg: '#dcfce7', color: '#16a34a', label: 'Sent'}
    case 'scheduled': return {bg: '#fef9c3', color: '#a16207', label: 'Scheduled'}
    default: return {bg: '#f1f5f9', color: '#64748b', label: s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Pending'}
  }
}

const fmtDateTime = (iso: string): string => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const truncate = (text: string, max = 50): string =>
  text.length > max ? text.slice(0, max).trimEnd() + '…' : text

const localNow = (): string => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const sendIcon = (size = 18, color = 'currentColor') => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='22' y1='2' x2='11' y2='13' />
    <polygon points='22 2 15 22 11 13 2 9 22 2' />
  </svg>
)
const bellIcon = (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#0f172a' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
    <path d='M13.73 21a2 2 0 0 1-3.46 0' />
  </svg>
)
const peopleIcon = (
  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#64748b' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' /><circle cx='9' cy='7' r='4' />
    <path d='M23 21v-2a4 4 0 0 0-3-3.87' /><path d='M16 3.13a4 4 0 0 1 0 7.75' />
  </svg>
)

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6,
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 14, color: '#0f172a',
  border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
}

const BroadcastPage: FC = () => {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<Audience>('all')
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduleDateTime, setScheduleDateTime] = useState('')

  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [history, setHistory] = useState<BroadcastNotification[]>([])
  const [loading, setLoading] = useState(true)

  const loadHistory = () => {
    setLoading(true)
    api.getBroadcasts()
      .then(setHistory)
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(loadHistory, [])

  const canSubmit =
    !!title.trim() && !!body.trim() && !sending && (!scheduleEnabled || !!scheduleDateTime)

  const send = async () => {
    setError('')
    setSuccess('')
    if (!title.trim() || !body.trim()) {
      setError('Title and message are required.')
      return
    }
    if (scheduleEnabled && !scheduleDateTime) {
      setError('Please choose a date and time to schedule.')
      return
    }
    setSending(true)
    try {
      await api.sendBroadcast({
        title: title.trim(),
        body: body.trim(),
        audience,
        scheduledAt: scheduleEnabled && scheduleDateTime ? new Date(scheduleDateTime).toISOString() : undefined,
      })
      const scheduled = scheduleEnabled && scheduleDateTime
      setSuccess(
        scheduled
          ? 'Notification scheduled successfully.'
          : 'Notification sent successfully.'
      )
      toast(scheduled ? 'Notification scheduled' : 'Notification sent')
      setTitle('')
      setBody('')
      setAudience('all')
      setScheduleEnabled(false)
      setScheduleDateTime('')
      loadHistory()
    } catch (e: any) {
      setError(e?.message ?? 'Failed to send notification.')
      toast(e?.message ?? 'Send notification failed', 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 28}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Notifications</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Send push notifications and manage notification history
        </p>
      </div>

      {/* Broadcast Notification */}
      <div style={{...CARD, padding: 24, marginBottom: 24}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20}}>
          <span style={{color: '#3b82f6', display: 'flex'}}>{sendIcon(18, '#3b82f6')}</span>
          <h3 style={{fontSize: 16, fontWeight: 600, color: '#0f172a', margin: 0}}>Broadcast Notification</h3>
        </div>

        {error ? (
          <div style={{background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16}}>
            {error}
          </div>
        ) : null}
        {success ? (
          <div style={{background: '#dcfce7', color: '#16a34a', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16}}>
            {success}
          </div>
        ) : null}

        {/* Title */}
        <div style={{marginBottom: 18}}>
          <label style={labelStyle}>Title</label>
          <input
            style={inputStyle}
            value={title}
            maxLength={80}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='Notification title'
          />
        </div>

        {/* Message */}
        <div style={{marginBottom: 18}}>
          <label style={labelStyle}>Message</label>
          <textarea
            style={{...inputStyle, minHeight: 96, resize: 'vertical'}}
            value={body}
            maxLength={200}
            rows={4}
            onChange={(e) => setBody(e.target.value)}
            placeholder='Write your notification message…'
          />
          <div style={{fontSize: 12, color: '#94a3b8', marginTop: 6}}>{body.length}/200 characters</div>
        </div>

        {/* Target Audience */}
        <div style={{marginBottom: 18}}>
          <label style={labelStyle}>Target Audience</label>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12}}>
            {AUDIENCES.map((a) => {
              const selected = audience === a.value
              return (
                <button
                  key={a.value}
                  type='button'
                  onClick={() => setAudience(a.value)}
                  style={{
                    padding: '10px 0', fontSize: 14, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                    border: selected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                    background: selected ? '#eff6ff' : '#fff',
                    color: selected ? '#2563eb' : '#475569',
                    transition: 'all 0.15s',
                  }}
                >
                  {a.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Schedule for later */}
        <div style={{marginBottom: 18}}>
          <label style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#0f172a', cursor: 'pointer'}}>
            <input
              type='checkbox'
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
              style={{width: 16, height: 16, cursor: 'pointer'}}
            />
            Schedule for later
          </label>
          {scheduleEnabled ? (
            <input
              type='datetime-local'
              value={scheduleDateTime}
              min={localNow()}
              onChange={(e) => setScheduleDateTime(e.target.value)}
              style={{...inputStyle, marginTop: 10, maxWidth: 280}}
            />
          ) : null}
        </div>

        {/* Submit */}
        <button
          type='button'
          onClick={send}
          disabled={!canSubmit}
          style={{
            width: '100%', padding: '12px 0', fontSize: 15, fontWeight: 600, borderRadius: 8,
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: canSubmit ? '#3b82f6' : '#93c5fd',
            color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {sendIcon(17, '#fff')}
          {sending ? 'Sending…' : scheduleEnabled ? 'Schedule' : 'Send Now'}
        </button>
      </div>

      {/* Notification History */}
      <div style={{...CARD, overflow: 'hidden'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, padding: '20px 24px', borderBottom: '1px solid #f1f5f9'}}>
          {bellIcon}
          <h3 style={{fontSize: 16, fontWeight: 600, color: '#0f172a', margin: 0}}>Notification History</h3>
        </div>

        {loading ? (
          <div style={{padding: '40px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 14}}>Loading…</div>
        ) : history.length === 0 ? (
          <div style={{padding: '40px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 14}}>No notifications sent yet</div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{textAlign: 'left'}}>
                  {['TITLE', 'MESSAGE', 'TARGET', 'RECIPIENTS', 'DATE & TIME', 'STATUS'].map((h) => (
                    <th key={h} style={{
                      padding: '12px 24px', fontSize: 11, fontWeight: 700, color: '#94a3b8',
                      letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((b, i) => {
                  const ac = audienceColors(b.audience)
                  const sc = statusColors(b.status)
                  const when = b.status === 'scheduled' && b.scheduledAt ? b.scheduledAt : b.createdAt
                  return (
                    <tr key={b.id} style={{borderBottom: i < history.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
                      <td style={{padding: '14px 24px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap'}}>{b.title}</td>
                      <td style={{padding: '14px 24px', color: '#64748b', maxWidth: 280}}>{truncate(b.body)}</td>
                      <td style={{padding: '14px 24px'}}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: ac.bg, color: ac.color,
                        }}>
                          {audienceLabel(b.audience)}
                        </span>
                      </td>
                      <td style={{padding: '14px 24px', color: '#475569', whiteSpace: 'nowrap'}}>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                          {peopleIcon}
                          {(b.totalDevices || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{padding: '14px 24px', color: '#64748b', whiteSpace: 'nowrap'}}>{fmtDateTime(when)}</td>
                      <td style={{padding: '14px 24px'}}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: sc.bg, color: sc.color,
                        }}>
                          {sc.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

const BroadcastPageWrapper: FC = () => <BroadcastPage />

export {BroadcastPageWrapper}
